import React from 'react';
import { Row, Col, DatePicker, Input, Select, Space, Button } from 'antd';
import { SearchOutlined, ReloadOutlined, CalendarOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import './BatchFilters.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/**
 * BatchFilters - Filter component for batches
 * 
 * @param {Object} props
 * @param {Function} props.onDateRangeChange - Handler for date range changes
 * @param {Function} props.onSearchChange - Handler for search changes
 * @param {Function} props.onStatusFilterChange - Handler for status filter changes
 * @param {Function} props.onGroupByChange - Handler for group by changes
 * @param {Function} props.onClearFilters - Handler to clear all filters
 * @param {Array} props.dateRange - Current date range [startDate, endDate]
 * @param {string} props.searchValue - Current search value
 * @param {string} props.statusFilter - Current status filter
 * @param {string} props.groupBy - Current grouping ('month', 'week', 'year', 'encounter_date')
 */
const BatchFilters = ({
  onDateRangeChange,
  onSearchChange,
  onStatusFilterChange,
  onGroupByChange,
  onClearFilters,
  dateRange,
  searchValue,
  statusFilter,
  groupBy = 'month',
}) => {
  const handleDateRangeChange = (dates) => {
    if (onDateRangeChange) {
      onDateRangeChange(dates);
    }
  };

  const handleSearchChange = (e) => {
    if (onSearchChange) {
      onSearchChange(e.target.value);
    }
  };

  const handleStatusChange = (value) => {
    if (onStatusFilterChange) {
      onStatusFilterChange(value);
    }
  };

  const handleGroupByChange = (value) => {
    if (onGroupByChange) {
      onGroupByChange(value);
    }
  };

  const handleClear = () => {
    if (onClearFilters) {
      onClearFilters();
    }
  };

  const hasActiveFilters = dateRange || searchValue || statusFilter || (groupBy !== 'month');

  return (
    <div className="batch-filters">
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={12} md={8}>
          <div className="filter-item">
            <label className="filter-label">
              <CalendarOutlined style={{ marginRight: '4px' }} />
              Date Range
            </label>
            <RangePicker
              style={{ width: '100%' }}
              format="YYYY-MM-DD"
              value={dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null}
              onChange={handleDateRangeChange}
              placeholder={['Start Date', 'End Date']}
              allowClear
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <div className="filter-item">
            <label className="filter-label">
              <SearchOutlined style={{ marginRight: '4px' }} />
              Search
            </label>
            <Input
              placeholder="Search by encounter date, claim count..."
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={handleSearchChange}
              allowClear
            />
          </div>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <div className="filter-item">
            <label className="filter-label">Group By</label>
            <Select
              style={{ width: '100%' }}
              value={groupBy}
              onChange={handleGroupByChange}
            >
              <Option value="month">Month</Option>
              <Option value="week">Week</Option>
              <Option value="year">Year</Option>
              <Option value="encounter_date">Encounter Date</Option>
            </Select>
          </div>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <div className="filter-item">
            <label className="filter-label">Status</label>
            <Select
              style={{ width: '100%' }}
              placeholder="All Statuses"
              value={statusFilter}
              onChange={handleStatusChange}
              allowClear
            >
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="queried">Queried</Option>
              <Option value="declined">Declined</Option>
              <Option value="paid">Paid</Option>
            </Select>
          </div>
        </Col>

        {hasActiveFilters && (
          <Col xs={24} sm={12} md={4}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleClear}
              style={{ marginTop: '24px' }}
            >
              Clear Filters
            </Button>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default BatchFilters;

